import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  CircularProgress,
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import { LocationOn as LocationOnIcon, Add as AddIcon } from '@mui/icons-material';

const AddressSearch = ({
  value,
  onChange,
  placeholder = "Rechercher votre adresse complète...",
  sx = {},
  countries = [], // Empty array means all countries
  theme = 'dark' // 'dark' for signup forms, 'light' for home page
}) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    // Close suggestions when clicking outside
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const searchAddresses = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Build the Nominatim API URL
      let url = `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(searchQuery)}&` +
        `format=json&` +
        `limit=8&` +
        `addressdetails=1&` +
        `extratags=1`;

      // Add country codes if specified
      if (countries.length > 0) {
        url += `&countrycodes=${countries.join(',')}`;
      }

      const response = await fetch(url);

      if (response.ok) {
        const data = await response.json();
        
        // Format results to show complete addresses
        const formattedSuggestions = data
          .filter(item => item.address && item.display_name)
          .map(item => {
            const address = item.address;
            
            // Build a clean address string
            const parts = [];
            
            // Add house number and street
            if (address.house_number && address.road) {
              parts.push(`${address.house_number} ${address.road}`);
            } else if (address.road) {
              parts.push(address.road);
            }
            
            // Add city/town/village
            const city = address.city || address.town || address.village || address.municipality;
            if (city) {
              parts.push(city);
            }
            
            // Add postal code
            if (address.postcode) {
              parts.push(address.postcode);
            }
            
            // Add country
            if (address.country) {
              parts.push(address.country);
            }
            
            const cleanAddress = parts.join(', ');
            
            return {
              id: item.place_id,
              display_name: item.display_name,
              formatted: cleanAddress,
              full_address: item.display_name,
              address_components: {
                house_number: address.house_number || '',
                road: address.road || '',
                city: city || '',
                postcode: address.postcode || '',
                state: address.state || '',
                country: address.country || '',
                country_code: address.country_code || ''
              },
              coordinates: {
                lat: parseFloat(item.lat),
                lon: parseFloat(item.lon)
              }
            };
          })
          // Remove duplicates based on formatted address
          .filter((item, index, self) => 
            index === self.findIndex(t => t.formatted === item.formatted)
          );

        setSuggestions(formattedSuggestions);
      }
    } catch (error) {
      console.error('Error searching addresses:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const newQuery = event.target.value;
    setQuery(newQuery);
    setShowSuggestions(true);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce the search
    debounceRef.current = setTimeout(() => {
      searchAddresses(newQuery);
    }, 300);
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.formatted);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Call the onChange callback with the selected address
    if (onChange) {
      onChange({
        query: suggestion.formatted,
        full_address: suggestion.full_address,
        address_components: suggestion.address_components,
        coordinates: suggestion.coordinates
      });
    }
  };

  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (suggestions.length > 0) {
        handleSuggestionClick(suggestions[0]);
      } else if (query.trim().length > 0) {
        // Allow manual entry if no suggestions found
        handleManualAddress();
      }
    }
  };

  const handleManualAddress = () => {
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Call the onChange callback with the manual address
    if (onChange) {
      onChange({
        query: query,
        full_address: query,
        address_components: {
          house_number: '',
          road: '',
          city: '',
          postcode: '',
          state: '',
          country: '',
          country_code: ''
        },
        coordinates: null,
        is_manual: true
      });
    }
  };

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%', ...sx }}>
      <TextField
        fullWidth
        placeholder={placeholder}
        value={query}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LocationOnIcon sx={{ color: theme === 'dark' ? '#f5f5dc' : '#666' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {loading ? (
                <CircularProgress size={20} sx={{ color: theme === 'dark' ? '#f5f5dc' : '#666' }} />
              ) : (
                query.length >= 2 && (
                  <Tooltip title="Utiliser cette adresse" arrow>
                    <IconButton
                      size="small"
                      onClick={handleManualAddress}
                      sx={{
                        color: theme === 'dark' ? '#f5f5dc' : '#666',
                        '&:hover': {
                          backgroundColor: theme === 'dark' ? 'rgba(245,245,220,0.1)' : 'rgba(0,0,0,0.04)',
                        }
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              )}
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#f8f9fa',
            color: theme === 'dark' ? 'white' : '#333',
            border: theme === 'light' ? 'none' : undefined,
            '& fieldset': {
              borderColor: theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'transparent',
            },
            '&:hover fieldset': {
              borderColor: theme === 'dark' ? '#f5f5dc' : '#ddd',
            },
            '&:hover': {
              backgroundColor: theme === 'light' ? '#f0f1f2' : undefined,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme === 'dark' ? '#f5f5dc' : '#666',
            },
            '&.Mui-focused': {
              backgroundColor: theme === 'light' ? '#fff' : undefined,
              boxShadow: theme === 'light' ? '0 0 0 2px rgba(0,0,0,0.1)' : undefined,
            },
          },
          '& .MuiInputBase-input::placeholder': {
            color: theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
            opacity: 1,
          },
          ...sx
        }}
      />
      
      {showSuggestions && (suggestions.length > 0 || (query.length >= 2 && !loading)) && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 2147483647, // Maximum z-index value
            maxHeight: 300,
            overflowY: 'auto',
            overflowX: 'hidden',
            mt: 0.5,
            backgroundColor: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(245,245,220,0.6)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(245,245,220,0.8)',
              },
            },
          }}
        >
          <List dense>
            {suggestions.map((suggestion) => (
              <ListItem
                key={suggestion.id}
                button
                onClick={() => handleSuggestionClick(suggestion)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(245,245,220,0.1)'
                  }
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {suggestion.formatted}
                    </Typography>
                  }
                  secondary={
                    suggestion.full_address !== suggestion.formatted && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {suggestion.full_address}
                      </Typography>
                    )
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
      
      {showSuggestions && query.length >= 2 && suggestions.length === 0 && !loading && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 2147483647, // Maximum z-index value
            mt: 0.5,
            p: 2,
            backgroundColor: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)'
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            Aucune adresse trouvée dans OpenStreetMap.
            <br />
            <Typography component="span" sx={{ color: '#f5f5dc', fontWeight: 500 }}>
              Cliquez sur le bouton + pour utiliser cette adresse.
            </Typography>
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AddressSearch;